<template>
  <div
    class="modal"
    :class="{ 'is-active': isActive }"
  >
    <div
      class="modal-background"
      @click="close()"
    />
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          Upload Meeting Recording
        </p>
        <button
          class="delete"
          aria-label="close"
          @click="close()"
        />
      </header>
      <section class="modal-card-body">
        <div class="field">
          <label class="label">Upload Recording</label>
          <p class="help mb-4">
            Upload a meeting recording from your device
          </p>
          <div class="file has-name is-fullwidth">
            <label class="file-label">
              <input
                class="file-input"
                type="file"
                accept="video/mp4,audio/mp3,audio/wav"
                @change="handleFileUpload"
              >
              <span class="file-cta">
                <span class="file-icon">
                  <i class="fas fa-upload" />
                </span>
                <span class="file-label">Choose file...</span>
              </span>
              <span class="file-name">
                {{ selectedFile ? selectedFile.name : 'No file selected' }}
              </span>
            </label>
          </div>
          <p class="help mt-2">
            Supported formats: MP4, MP3, WAV<br>
            Maximum file size: 2GB
          </p>
        </div>

        <!-- Upload Recording -->
        <button
          v-if="selectedFile"
          class="button is-primary is-fullwidth mt-5"
          :class="{ 'is-loading': isUploading }"
          :disabled="isUploading"
          @click="handleS3Upload"
        >
          Upload Recording
        </button>

        <!-- Upload Complete -->
        <button
          v-if="uploadComplete && !uploadFailed"
          class="button is-success is-fullwidth mt-5"
          @click="close()"
        >
          <i class="fas fa-check tw-pr-2" />
          <span>Upload Complete</span>
        </button>

        <!-- Upload Progress -->
        <progress
          v-if="isUploading"
          class="progress is-primary mt-5"
          :value="percentComplete"
          max="100"
        >
          {{ percentComplete }}%
        </progress>

        <!-- Upload Complete Message -->
        <div
          v-if="uploadComplete && !uploadFailed"
          class="tw-px-3 tw-pt-5 tw-flex tw-flex-col tw-items-center"
        >
          <!-- <i class="fas fa-check tw-text-emerald-500 tw-mb-1 tw-text-xl" /> -->
          <p class="tw-text-gray-500 tw-text-sm">
            Your upload is complete.
            The meeting recording will continue to process in the background.
            The meeting will appear in your list once processing is complete.
            It's now safe to close this window.
          </p>
        </div>

        <!-- Upload Failed Message -->
        <div
          v-if="uploadFailed"
          class="tw-px-3 tw-pt-5 tw-flex tw-flex-col tw-items-center"
        >
          <i class="fas fa-exclamation-triangle tw-text-red-700 tw-mb-1 tw-text-xl" />
          <p class="tw-text-gray-700 tw-text-sm">
            Something went wrong while trying to upload this file.
            The YourStake team has been notified and will work on resolving this issue.
            In the meantime, feel try to try again or contact support.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { awaitTask } from '../../checkup/helpers/tasks';
import { uploadToS3 } from '@frontend/shared/multipart-s3-uploads.js';

export default {
  name: 'ManualUploadModal',
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    entityExternalId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedFile: null,
      isUploading: false,
      percentComplete: 0,
      timeRemaining: 0,
      statusMessage: null,
      uploadComplete: false,
      uploadFailed: false,
    };
  },
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
  },
  watch: {
    isActive(newVal) {
      if (!newVal) {
        this.selectedFile = null;
        this.$emit('meeting-created'); // this just gets meetings again
      }
    },
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];

      // file.size returns the size in bytes
      // 2e9 is equal to 2 billion bytes, or 2GB
      if (file.size > 2e9) {
        alert(`The file is too large. The limit is 2GB.`);
        return;
      }

      this.selectedFile = file;
    },
    async uploadFile() {
      if (!this.selectedFile) {
        alert('Please select a file first');
        return;
      }

      this.isUploading = true;

      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        formData.append('entity_external_id', this.entityExternalId);
        console.log('UPLOADING TASK');
        const response = await awaitTask('/api/v1/meetings/upload/', {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value,
          },
        });
        console.log('UPLOADING TASK RESPONSE', response);

        if (response.task.status === 'complete') {
          this.$emit('meeting-created');
          this.close();
          this.selectedFile = null;
        } else {
          console.error('File upload failed:', response);
          alert('File upload failed. Please try again.');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('An error occurred while uploading the file. Please try again.');
      } finally {
        this.isUploading = false;
      }
    },
    async handleS3Upload() {
      try {
        this.uploadFailed = false;
        const data = await this.prepareForUpload();

        if (!data.s3_key || !data.meeting_id) {
          throw new Error(`Failed to prepare upload. Response: ${JSON.stringify(data)}`);
        }

        this.isUploading = true;

        await uploadToS3(this.selectedFile, data.s3_key, 5, status => {
          this.percentComplete = status.percentComplete;
        });

        this.isUploading = false;
        this.uploadComplete = true;
        this.selectedFile = null;

        // Now that the recording is uploaded, tell the backend to start processing the meeting.
        const created = await this.startProcessing(data.meeting_id);

        this.close(created);
        //
      } catch (error) {
        console.error('Failed to upload recording to S3', error);
        this.isUploading = false;
        this.uploadFailed = true;
        throw error;
      }
    },
    close(created=false) {
      this.isUploading = false;
      this.uploadComplete = false;
      this.uploadFailed = false;
      this.selectedFile = null;
      this.statusMessage = null;

      if (created) this.$emit('meeting-created');
      this.$emit('update:isActive', false);
    },
    async prepareForUpload() {
      const body = {
        file_name: this.selectedFile.name,
        content_type: this.selectedFile.type,
      };

      if (this.entityExternalId) {
        body.entity_external_id = this.entityExternalId;
      }

      const res = await fetch('/api/v1/meetings/upload/prepare/', {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(body),
      });
      return res.json();
    },
    /* One shot upload
    async uploadToS3(url) {
      this.isUploading = true;
      const res = await fetch(url, {
        method: 'PUT',
        body: this.selectedFile,
      });
      console.log('S3 upload response:', res);
      this.isUploading = false;
    }, */
    async startProcessing(id) {
      const res = await awaitTask(`/api/v1/meetings/${id}/process/`, {
        method: 'GET',
        headers: this.headers,
      });

      return res.task.status === 'complete';
    },
  },
};
</script>

<style scoped>
.file-cta, .file-name {
  border: 1px solid #dbdbdb;
}

.file-name {
  border-left: none;
  max-width: none;
}

.modal-card {
  max-width: 480px;
}
</style>
